import type { UserNotification } from "./models";

/**
 * Synthesizes the in-app route to navigate to when a notification row is
 * tapped. Mirrors iOS `Views/NotificationView.swift::deepLinkURL(for:)`
 * (lines ~94-116) and Android `NotificationCenterSheet.kt::deepLinkUrlFor`
 * (lines ~65-77), so push tap behavior matches what the user sees in the
 * Notification Center sheet.
 *
 * Web path differences vs iOS/Android:
 *   - `chore_assigned` falls back to `/chores` (web has no per-chore detail
 *     route; iOS uses `/assigned_chores/{id}`).
 *   - `member_joined` routes to `/account/households` (web's equivalent of
 *     iOS `/member_dashboard`).
 *   - `event` has no detail page yet, so we route to the list `/events`.
 *   - `responsibility_*` types route to `/responsibilities` (no detail page).
 *
 * When `related_id` is missing for a type that needs it (post, chat), the
 * helper falls back to the parent list route rather than emitting a broken
 * `/posts/undefined` URL. Unknown types fall back to `/dashboard` so the
 * user lands somewhere reasonable instead of a 404.
 */
export function routeForNotification(notification: UserNotification): string {
  const type = (notification.view_model_type || "").toLowerCase();
  const id = notification.related_id;

  switch (type) {
    case "post":
    case "post_created":
    case "post_reply":
      return id ? `/posts/${id}` : "/posts";
    case "chat":
    case "chat_created":
    case "chat_reply":
    case "message":
      return id ? `/chats/${id}` : "/chats";
    case "event":
    case "event_created":
      // Web has no per-event detail page yet; route to the list view.
      return "/events";
    case "responsibility_claimed":
    case "responsibility_completed":
    case "responsibility_reschedule":
    case "responsibility_reminder":
      return "/responsibilities";
    case "chore_assigned":
      // Web has no /assigned_chores/{id} detail route; fall back to /chores.
      return "/chores";
    case "member_joined":
      // Web equivalent of iOS `/member_dashboard`.
      return "/account/households";
    default:
      return "/dashboard";
  }
}
